// src/components/admin/ToolForm.jsx
import React, { useState } from 'react';
import { useRoles } from '../../hooks/useRoles';
import { useCategories } from '../../hooks/useCategories';
import DesktopBuilds from './DesktopBuilds';
import ToolIconField from './ToolIconField';
import {
  Button,
  Field,
  ModalActions,
  Select,
  Spinner,
  TextArea,
  TextInput,
  cx,
} from '../common/ui';

// "Brand Studio" -> "brand-studio". Only used to prefill; it stays editable.
const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const ToolForm = ({ tool, onSubmit, onCancel, isSaving }) => {
  const { roles, isLoading: rolesLoading } = useRoles();
  const { categories, isLoading: categoriesLoading } = useCategories();

  const isNew = !tool?._id;

  const [form, setForm] = useState({
    id: tool?.id || '',
    name: tool?.name || '',
    description: tool?.description || '',
    url: tool?.url || '',
    category: tool?.category || '',
    tags: (tool?.tags || []).join(', '),
    allowedRoles: (tool?.allowedRoles || []).map((r) => r._id || r),
    icon: tool?.icon || 'FileText',
  });
  const [error, setError] = useState('');

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // Keep the id in step with the name until someone types their own.
  const onNameChange = (name) => {
    set(isNew && (!form.id || form.id === slugify(form.name)) ? { name, id: slugify(name) } : { name });
  };

  const toggleRole = (roleId) =>
    set({
      allowedRoles: form.allowedRoles.includes(roleId)
        ? form.allowedRoles.filter((r) => r !== roleId)
        : [...form.allowedRoles, roleId],
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.allowedRoles.length === 0) {
      setError('Pick at least one role, or nobody will be able to see this tool.');
      return;
    }

    try {
      await onSubmit({
        ...form,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not save this tool');
    }
  };

  if (rolesLoading || categoriesLoading) return <Spinner label="Loading roles…" />;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {error && (
        <p className="rounded-lg bg-danger-soft px-3 py-2.5 text-[13px] text-danger">{error}</p>
      )}

      <Field label="Name" htmlFor="tool-name">
        <TextInput
          id="tool-name"
          value={form.name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Document Analyzer"
          required
        />
      </Field>

      <Field
        label="Tool ID"
        htmlFor="tool-id"
        hint="Used in links and search. Lowercase, no spaces."
      >
        <TextInput
          id="tool-id"
          value={form.id}
          onChange={(e) => set({ id: e.target.value })}
          className="font-mono text-[13px]"
          placeholder="document-analyzer"
          required
        />
      </Field>

      <Field
        label="Description"
        htmlFor="tool-desc"
        hint="One sentence, written for whoever is deciding whether to click it."
      >
        <TextArea
          id="tool-desc"
          rows={2}
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
          placeholder="Pull summaries and action items out of PDFs and decks."
          required
        />
      </Field>

      <Field label="Destination URL" htmlFor="tool-url">
        <TextInput
          id="tool-url"
          type="url"
          value={form.url}
          onChange={(e) => set({ url: e.target.value })}
          className="font-mono text-[13px]"
          placeholder="https://bestinbrands.com/document-analyzer"
          required
        />
      </Field>

      {isNew ? (
        <p className="rounded-lg border border-dashed border-line-strong px-3 py-2.5 text-[13px] text-ink-3">
          Shipping a desktop version too? Add the tool first — the installer uploader appears here
          once it exists.
        </p>
      ) : (
        <DesktopBuilds tool={tool} />
      )}

      <Field label="Category" htmlFor="tool-category" hint="Sets which group it appears under.">
        <Select
          id="tool-category"
          value={form.category}
          onChange={(e) => set({ category: e.target.value })}
          required
        >
          <option value="">Choose a category…</option>
          {categories.map((category) => (
            <option key={category.key} value={category.key}>
              {category.label}
            </option>
          ))}
        </Select>
      </Field>

      <ToolIconField tool={tool} value={form.icon} onChange={(icon) => set({ icon })} />

      <Field
        label="Tags"
        htmlFor="tool-tags"
        hint="Optional. Comma separated — these are matched by search but never shown."
      >
        <TextInput
          id="tool-tags"
          value={form.tags}
          onChange={(e) => set({ tags: e.target.value })}
          placeholder="pdf, summarise, contracts"
        />
      </Field>

      <Field
        label="Who can see it"
        error={form.allowedRoles.length === 0 ? 'Pick at least one role.' : ''}
      >
        <div className="flex flex-wrap gap-2">
          {roles.map((role) => {
            const on = form.allowedRoles.includes(role._id);
            return (
              <button
                key={role._id}
                type="button"
                aria-pressed={on}
                onClick={() => toggleRole(role._id)}
                className={cx(
                  'rounded-lg border px-3 py-1.5 text-[13px] capitalize transition-colors',
                  on
                    ? 'border-accent-line bg-accent-soft font-medium text-accent'
                    : 'border-line-strong bg-surface text-ink-2 hover:bg-surface-2'
                )}
              >
                {role.name}
              </button>
            );
          })}
        </div>
      </Field>

      <ModalActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={isSaving}>
          {isSaving ? 'Saving…' : isNew ? 'Add tool' : 'Save changes'}
        </Button>
      </ModalActions>
    </form>
  );
};

export default ToolForm;
